import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import type { Downloader, DownloadProgressHandler } from '@mimir/common/types/downloader';
import type { AudioTsSegmentRange, FlatFileReference, TruncatedMp4, TsSegmentRange } from '@mimir/primitive/artifactReference';
import type { BackendOps } from '../util/backendOperations';
import { ItemUrlProvider } from '../util/itemUrlProvider';
import { fetchTransferUrl } from '../storageManager/resolveTransferUrl';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';

export type ExplicitArtifactReference =
  FlatFileReference |
  TsSegmentRange |
  AudioTsSegmentRange |
  TruncatedMp4;

export async function downloadItemArtifact(
  services: {
    downloader: Downloader,
    backendOps: BackendOps,
  },
  artifactReference: ExplicitArtifactReference,
  itemArtefact: ItemArtefact,
  outputFilename: string,
  abortSignal: AbortSignal,
  progressHandler: DownloadProgressHandler | undefined,
): Promise<boolean> {
  switch (artifactReference.type) {
    case 'FlatFileReference': {
      return await downloadFlatFileArtifact(services, artifactReference, itemArtefact, outputFilename, abortSignal, progressHandler);
    }
    case 'AudioTsSegmentRange':
    case 'TsSegmentRange': {
      if (itemArtefact === 'aaf') {
        throw new Error(`Invalid artifact ${itemArtefact} for artefact reference of type ${artifactReference.type}`);
      }
      return await downloadTsSegmentRangeArtifact(services, artifactReference, itemArtefact, outputFilename, abortSignal, progressHandler);
    }
    case 'TruncatedMp4': {
      if (itemArtefact === 'aaf') {
        throw new Error(`Invalid artifact ${itemArtefact} for artefact reference of type ${artifactReference.type}`);
      }
      return await downloadTruncatedMp4Artifact(services, artifactReference, itemArtefact, outputFilename, abortSignal, progressHandler);
    }
  }
}

async function downloadFlatFileArtifact(
  services: {
    backendOps: BackendOps,
    downloader: Downloader,
  },
  artifactReference: FlatFileReference,
  itemArtefact: ItemArtefact,
  outputFilename: string,
  abortSignal: AbortSignal,
  progressHandler: DownloadProgressHandler | undefined,
): Promise<boolean> {
  const { backendOps, downloader } = services;

  const url = await fetchTransferUrl(
    artifactReference.itemId,
    itemArtefact,
    backendOps,
  );
  if (!url) {
    throw new Error(`Could not find URL for ${itemArtefact} artifact for item ${artifactReference.itemId}`);
  }

  await downloader.downloadFlatFile({
    urlProvider: new ItemUrlProvider(
      url,
      async () => {
        const freshUrl = await fetchTransferUrl(artifactReference.itemId, itemArtefact, backendOps);
        if (!freshUrl) {
          throw new Error(`Could not find URL for ${itemArtefact} artifact for item ${artifactReference.itemId}`);
        }
        return freshUrl;
      }
    ),
    progressHandler,
    abortSignal,
    outputFilename,
  });

  return true;
}


async function downloadTsSegmentRangeArtifact(
  services: {
    downloader: Downloader,
    backendOps: BackendOps,
  },
  artifactReference: TsSegmentRange | AudioTsSegmentRange,
  itemArtefact: 'highres' | 'proxy',
  outputFilename: string,
  abortSignal: AbortSignal,
  progressHandler: DownloadProgressHandler | undefined,
): Promise<boolean> {
  if ((itemArtefact === 'highres') && (artifactReference.type === 'TsSegmentRange' && artifactReference.highResExt !== undefined)) {
    if (artifactReference.type === 'TsSegmentRange' && artifactReference.useIndex) {
      const { downloader } = services;
      await downloader.downloadMxfSubclipBasedOnProxySegmentRange({
        itemId: artifactReference.itemId,
        proxySegmentRange: {
          startIndexInclusive: artifactReference.segmentTimes.first,
          endIndexExclusive: artifactReference.segmentTimes.last + 1,
        },
        outputFilename,
        abortSignal,
        progressHandler,
      });
    } else {
      await downloadFlatFileArtifact(
        services,
        {
          type: 'FlatFileReference',
          itemId: artifactReference.itemId,
        },
        'highres',
        outputFilename,
        abortSignal,
        progressHandler,
      );
    }
  } else {
    const { downloader } = services;
    let streamRoleId: StreamRoleId.LiveRecordHighresTs | StreamRoleId.LiveRecordWebproxyTs | StreamRoleId.LiveRecordAudioOnlyHighresTs;
    if (artifactReference.type === 'AudioTsSegmentRange') {
      streamRoleId = StreamRoleId.LiveRecordAudioOnlyHighresTs;
    } else {
      streamRoleId = (itemArtefact === 'highres') ? StreamRoleId.LiveRecordHighresTs : StreamRoleId.LiveRecordWebproxyTs;
    }
    await downloader.downloadTsSegmentStreamAsSingleFile({
      itemId: artifactReference.itemId,
      segmentRange: {
        startIndexInclusive: artifactReference.segmentTimes.first,
        endIndexExclusive: artifactReference.segmentTimes.last + 1,
      },
      streamRoleId,
      outputFilename,
      abortSignal,
      remuxTo: artifactReference.remuxTo,
      progressHandler,
    });
  }
  return true;
}

async function downloadTruncatedMp4Artifact(
  services: {
    backendOps: BackendOps,
    downloader: Downloader,
  },
  artifactReference: TruncatedMp4,
  itemArtefact: 'highres' | 'proxy',
  outputFilename: string,
  abortSignal: AbortSignal,
  progressHandler: DownloadProgressHandler | undefined,
): Promise<boolean> {
  if (itemArtefact === 'highres') {
    await downloadFlatFileArtifact(
      services,
      {
        type: 'FlatFileReference',
        itemId: artifactReference.itemId,
      },
      'highres',
      outputFilename,
      abortSignal,
      progressHandler,
    );
  } else {
    const { downloader } = services;
    await downloader.downloadFragmentedMp4StreamToSingleFile({
      itemId: artifactReference.itemId,
      segmentRange: {
        startIndexInclusive: 0,
        // Note: lastSegmentIndex is inclusive and endIndex is exclusive so it
        // seems we should add 1 here, but lastSegmentIndex also includes the
        // initialization segment which means we should subtract 1 so this
        // cancels out.
        endIndexExclusive: artifactReference.lastSegmentIndex,
      },
      outputFilename,
      abortSignal,
      progressHandler,
    });
  }
  return true;
}
